import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, from, firstValueFrom, switchMap, catchError, of, throwError, skip, take } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ILogger } from '../../interfaces/logger.interface';
import { LoggerFactory } from '../../utils/logger-factory';

/**
 * Service responsible for managing token refresh state and queuing requests during refresh.
 * This service ensures only one token refresh happens at a time and queues other requests.
 * Implements proactive token refresh to prevent race conditions.
 */
@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {
  private readonly _logger: ILogger = LoggerFactory.getLogger('TokenRefreshService');
  private readonly _authService = inject(AuthService);

  private _isRefreshing = false;
  // Initialize with current token to avoid null emissions for waiting requests
  private _refreshSubject = new BehaviorSubject<string | null>(this._authService.getAccessTokenSync());

  /**
   * Gets the current access token, refreshing proactively if necessary.
   * If a refresh is already in progress, waits for it to complete.
   *
   * @returns Observable that emits the access token or null if refresh failed
   */
  public getAccessToken(): Observable<string | null> {
    // If already refreshing, wait for the refresh to complete and get the latest value
    if (this._isRefreshing) {
      this._logger.writeDebug('Token refresh in progress, waiting for completion');
      // Skip the current value and wait for the next emission (the new token after refresh completes)
      return this._refreshSubject.asObservable().pipe(skip(1), take(1));
    }

    // Check if token is expired or close to expiring (proactive refresh)
    if (this._authService.isTokenExpired() || this._authService.isTokenExpiringSoon()) {
      return this.refreshToken();
    }

    // Token is still valid, return it
    const token = this._authService.getAccessTokenSync();
    // Update subject with current token in case it changed
    if (this._refreshSubject.value !== token) {
      this._refreshSubject.next(token);
    }
    return of(token);
  }

  /**
   * Refreshes the token and notifies all waiting requests.
   * Only one refresh can happen at a time.
   *
   * @returns Observable that emits the new access token or null if refresh failed
   */
  private refreshToken(): Observable<string | null> {
    if (this._isRefreshing) {
      this._logger.writeDebug('Token refresh already in progress, waiting for completion');
      // Skip the current value and wait for the next emission (the new token after refresh completes)
      return this._refreshSubject.asObservable().pipe(skip(1), take(1));
    }

    this._isRefreshing = true;
    this._logger.writeDebug('Starting token refresh');

    return from(this._authService.refreshTokenOnFailure()).pipe(
      switchMap((success) => {
        if (success) {
          const newToken = this._authService.getAccessTokenSync();
          this._logger.writeDebug('Token refresh successful');
          // Emit new token to all waiting subscribers
          this._refreshSubject.next(newToken);
          return of(newToken);
        } else {
          this._logger.writeError('Token refresh failed');
          this._refreshSubject.next(null);
          return of(null);
        }
      }),
      catchError((error) => {
        this._logger.writeError('Token refresh error', error);
        this._refreshSubject.next(null);
        return of(null);
      }),
      switchMap((token) => {
        // Reset refresh state after emitting the token
        this._isRefreshing = false;
        return of(token);
      })
    );
  }

  /**
   * Checks if a token refresh is currently in progress.
   *
   * @returns true if refresh is in progress, false otherwise
   */
  public isRefreshing(): boolean {
    return this._isRefreshing;
  }

  /**
   * Resets in-memory token state so no stale token is used or refreshed.
   * Call when clearing auth for a new registration flow (e.g. Stripe entry).
   */
  public clearTokenState(): void {
    this._isRefreshing = false;
    this._refreshSubject.next(null);
  }
}
